
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import ImageImportStatus from "@/components/admin/ImageImportStatus";
import { 
  parseImageText, 
  processAndUploadImage, 
  ParsedImage 
} from "@/utils/imageImportUtils";

const ImageImportForm = () => {
  const { toast } = useToast();
  const [blogTitle, setBlogTitle] = useState("");
  const [imageText, setImageText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [proxyError, setProxyError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!blogTitle.trim()) {
      toast({
        title: "Blog title required",
        description: "Please enter a blog title for these images",
        variant: "destructive",
      });
      return;
    }
    
    if (!imageText.trim()) {
      toast({
        title: "Image information required",
        description: "Please enter the image URLs, alt text, and titles",
        variant: "destructive",
      });
      return;
    }
    
    const images = parseImageText(imageText);
    
    if (images.length === 0) {
      toast({
        title: "No valid images found",
        description: "Please check the format of your image information",
        variant: "destructive",
      });
      return;
    }
    
    setTotalCount(images.length);
    setProcessedCount(0);
    setIsProcessing(true);
    setProxyError(null);
    
    try {
      for (const image of images) {
        try {
          await processAndUploadImage(image, blogTitle);
          setProcessedCount(prev => prev + 1);
        } catch (err: any) {
          console.error("Error processing image", image.url, ":", err);
          
          // Set proxy error if it's a proxy-related error
          if (err.message?.includes('proxy') || err.message?.includes('Edge function')) {
            setProxyError(err.message);
          }
          
          toast({
            title: "Error processing image",
            description: `Failed to process ${image.url}: ${err.message}`,
            variant: "destructive",
          });
          // Continue with next image
        }
      }
      
      toast({
        title: "Images imported successfully",
        description: `Successfully imported ${processedCount} of ${images.length} images`,
      });
      
      if (processedCount > 0) {
        setBlogTitle("");
        setImageText("");
      }
    } catch (error: any) {
      console.error("Error importing images:", error);
      toast({
        title: "Error importing images",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <ImageImportStatus 
        isProcessing={isProcessing}
        processedCount={processedCount}
        totalCount={totalCount}
        proxyError={proxyError}
      />
      
      <div className="space-y-2">
        <Label htmlFor="blogTitle">Blog Post Title</Label>
        <Input
          id="blogTitle"
          value={blogTitle}
          onChange={e => setBlogTitle(e.target.value)}
          placeholder="Enter the blog post title these images belong to"
          required
        />
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="imageText">Image Information</Label>
        <Textarea
          id="imageText"
          value={imageText}
          onChange={e => setImageText(e.target.value)}
          placeholder="Enter the image information in the format:
url: https://example.com/image.jpg
alt text: Description of the image
title: Title of the image

url: https://example.com/another-image.jpg
alt text: Description of another image
title: Title of another image"
          className="min-h-[200px]"
          required
        />
        <p className="text-sm text-gray-500">
          Enter one or more images. Each image should start with a URL line, followed by alt text and title.
          <br />
          <strong>Note:</strong> For Bubble.io images, we'll try to use our proxy service to fetch them, but there may still be limitations.
        </p>
      </div>
      
      <Button 
        type="submit" 
        className="bg-elfGreen hover:bg-elfGreen-dark" 
        disabled={isProcessing}
      >
        {isProcessing ? "Importing Images..." : "Import Images"}
      </Button>
    </form>
  );
};

export default ImageImportForm;
